package com.budgeyet.feature.dashboard.data

import com.budgeyet.core.network.HouseholdRequestContextProvider
import com.budgeyet.core.network.mapper.toDomain
import com.budgeyet.feature.dashboard.data.mapper.toDomain
import com.budgeyet.feature.dashboard.data.remote.DashboardApiService
import com.budgeyet.feature.dashboard.domain.DashboardRepository
import com.budgeyet.feature.dashboard.domain.model.DashboardData

// Matches the "Family Activity" preview's fixture size (dummyDashboardActivityFeed takes 5) —
// this is a preview, not the full history (that's TransactionRepository/History screen).
private const val ACTIVITY_FEED_LIMIT = 5

// Real, network-backed DashboardRepository — same shape as RealCategoryRepository/
// RealTransactionRepository, resolving household id via HouseholdRequestContextProvider. Fans
// out to three endpoints (dashboard summary, household, activity feed) since no single backend
// response carries all of DashboardData — see DashboardApiService for why.
class RealDashboardRepository(
    private val api: DashboardApiService,
    private val contextProvider: HouseholdRequestContextProvider
) : DashboardRepository {

    override suspend fun getDashboard(): DashboardData {
        val (config, token, householdId) = contextProvider.get()
        val household = api.getHousehold(config, token, householdId).toDomain()
        var dashboard = api.getDashboard(config, token, householdId)

        if (dashboard.budget == null) {
            // New cycle with no budget yet — carry the previous cycle's forward so members
            // aren't stranded on the "Set Up Budget" screen every month. The backend no-ops
            // and returns null when there's nothing to carry (household never had a budget),
            // in which case the empty state stays as-is. runCatching: a rollover failure
            // (offline, or the rare concurrent-create 409) must never break the dashboard load.
            val carried = runCatching { api.rolloverBudget(config, token, householdId) }.getOrNull()
            if (carried != null) dashboard = api.getDashboard(config, token, householdId)
        }

        val activityFeed = api.getActivityFeed(config, token, householdId, limit = ACTIVITY_FEED_LIMIT)

        return DashboardData(
            household = household,
            budget = dashboard.budget?.toDomain(),
            categories = dashboard.categories.map { it.toDomain() },
            activityFeed = activityFeed.items.map { it.toDomain() }
        )
    }
}
